import { useState } from 'react'
import Modal from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import toast from 'react-hot-toast'
import { Copy, MessageCircle } from 'lucide-react'
import type { OrderResponse } from '@/services/ordersApi'
import {
  buildOrderWhatsAppNotificationMessage,
  buildOrderWhatsAppUrl,
  type WhatsAppNotificationType,
} from './ordersUx'

interface OrderWhatsAppShareModalProps {
  isOpen: boolean
  onClose: () => void
  order: OrderResponse
}

export default function OrderWhatsAppShareModal({
  isOpen,
  onClose,
  order,
}: OrderWhatsAppShareModalProps) {
  const defaultTemplate: WhatsAppNotificationType =
    order.status === 'ready'
      ? 'ready'
      : order.status === 'in_progress'
        ? 'in_progress'
        : order.status === 'completed'
          ? 'completed'
          : order.status === 'cancelled'
            ? 'cancelled'
            : 'confirmed'

  const [templateType, setTemplateType] = useState<WhatsAppNotificationType>(defaultTemplate)
  const [customText, setCustomText] = useState(() =>
    buildOrderWhatsAppNotificationMessage(order, defaultTemplate)
  )

  const handleSelectTemplate = (type: WhatsAppNotificationType) => {
    setTemplateType(type)
    setCustomText(buildOrderWhatsAppNotificationMessage(order, type))
  }

  const phone = order.customerPhone || order.contact?.phone || order.conversation?.waContact || ''
  const whatsappUrl = buildOrderWhatsAppUrl(phone, customText)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(customText)
      toast.success('Mensaje copiado al portapapeles')
    } catch {
      toast.error('No se pudo copiar el mensaje')
    }
  }

  const handleOpenWhatsApp = () => {
    if (!whatsappUrl) {
      toast.error('La orden no tiene un número de teléfono válido para WhatsApp')
      return
    }
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
    toast.success('Abriendo WhatsApp...')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Notificar cliente por WhatsApp" size="lg">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Selecciona una plantilla
          </label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
            <button
              type="button"
              onClick={() => handleSelectTemplate('confirmed')}
              className={[
                'rounded-lg border px-3 py-2 text-xs font-medium transition-all text-left',
                templateType === 'confirmed'
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="block font-semibold">Confirmada</span>
              <span className="text-[10px] text-slate-500">Pedido validado</span>
            </button>
            <button
              type="button"
              onClick={() => handleSelectTemplate('in_progress')}
              className={[
                'rounded-lg border px-3 py-2 text-xs font-medium transition-all text-left',
                templateType === 'in_progress'
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="block font-semibold">En camino</span>
              <span className="text-[10px] text-slate-500">Despacho activo</span>
            </button>
            <button
              type="button"
              onClick={() => handleSelectTemplate('ready')}
              className={[
                'rounded-lg border px-3 py-2 text-xs font-medium transition-all text-left',
                templateType === 'ready'
                  ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="block font-semibold">Lista</span>
              <span className="text-[10px] text-slate-500">Para retirar o en camino</span>
            </button>
            <button
              type="button"
              onClick={() => handleSelectTemplate('completed')}
              className={[
                'rounded-lg border px-3 py-2 text-xs font-medium transition-all text-left',
                templateType === 'completed'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="block font-semibold">Entregada</span>
              <span className="text-[10px] text-slate-500">Cierre exitoso</span>
            </button>
            <button
              type="button"
              onClick={() => handleSelectTemplate('cancelled')}
              className={[
                'rounded-lg border px-3 py-2 text-xs font-medium transition-all text-left',
                templateType === 'cancelled'
                  ? 'border-rose-500 bg-rose-50 text-rose-700 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="block font-semibold">Cancelada</span>
              <span className="text-[10px] text-slate-500">Aviso al cliente</span>
            </button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Mensaje a enviar
            </label>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 text-xs text-[#075f55] hover:underline"
            >
              <Copy className="h-3 w-3" />
              Copiar texto
            </button>
          </div>
          <textarea
            rows={5}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 focus:border-[#075f55] focus:bg-white focus:outline-none"
            placeholder="Escribe el mensaje..."
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <p className="font-semibold text-slate-800">Destinatario:</p>
          <p className="mt-0.5">
            {order.customerName || 'Cliente sin nombre'} ·{' '}
            <span className="font-mono text-slate-700">{phone || 'Sin teléfono asignado'}</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5"
          >
            <Copy className="h-4 w-4" />
            Copiar
          </Button>
          <Button
            type="button"
            onClick={handleOpenWhatsApp}
            disabled={!phone}
            className="inline-flex items-center gap-1.5 bg-[#25D366] text-white hover:bg-[#1EBE5D]"
          >
            <MessageCircle className="h-4 w-4" />
            Enviar por WhatsApp
          </Button>
        </div>
      </div>
    </Modal>
  )
}
